// packages/shared/src/time.ts — town time (§7.7, contract change 6)
import { tuning, type Hemisphere, type Season } from '@whiskerton/content';
import type { DayKey, HourKey, WeekKey } from './types/ids';
import type { LightPreset, TownTime } from './types/time';

type Parts = { year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number };
const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(timeZone: string): Intl.DateTimeFormat {
  let f = fmtCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short' });
    fmtCache.set(timeZone, f);
  }
  return f;
}
const WEEKDAYS: Readonly<Record<string, number>> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
function wallParts(epochMs: number, timeZone: string): Parts {
  const out: Record<string, string> = {};
  for (const p of fmt(timeZone).formatToParts(new Date(epochMs))) out[p.type] = p.value;
  return { year: Number(out.year), month: Number(out.month), day: Number(out.day), hour: Number(out.hour) % 24,
    minute: Number(out.minute), second: Number(out.second), weekday: WEEKDAYS[out.weekday ?? ''] ?? 0 };
}
const pad = (n: number, w = 2) => String(n).padStart(w, '0');
const ymd = (y: number, m: number, d: number) => `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
function addDays(y: number, m: number, d: number, n: number): [number, number, number] {
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return [t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()];
}
/** The zone's offset (ms) at an instant: wall clock read as UTC minus the instant. */
function offsetAt(epochMs: number, timeZone: string): number {
  const p = wallParts(epochMs, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(epochMs / 1000) * 1000;
}
/** Epoch of a wall time. In a DST gap → the first valid instant after it; in an overlap → the first occurrence. */
function wallToEpoch(y: number, m: number, d: number, h: number, mi: number, timeZone: string): number {
  const asUtc = Date.UTC(y, m - 1, d, h, mi);
  const o = offsetAt(asUtc - offsetAt(asUtc, timeZone), timeZone);
  const t = asUtc - o;
  const hits = (e: number) => { const q = wallParts(e, timeZone); return q.hour === h && q.minute === mi && q.day === d; };
  if (hits(t)) {
    // In an overlap the guess may land on either occurrence; the larger nearby offset gives the earlier one.
    const early = asUtc - Math.max(offsetAt(t - 6 * 3_600_000, timeZone), offsetAt(t + 6 * 3_600_000, timeZone));
    return early < t && hits(early) ? early : t;
  }
  const before = offsetAt(asUtc - o - 6 * 3_600_000, timeZone), after = offsetAt(asUtc - o + 6 * 3_600_000, timeZone);
  if (before === after) return t;
  let lo = asUtc - Math.max(before, after), hi = asUtc - Math.min(before, after);
  while (hi - lo > 1000) { const mid = Math.floor((lo + hi) / 2); if (offsetAt(mid, timeZone) === before) lo = mid; else hi = mid; }
  return Math.ceil(hi / 1000) * 1000;
}

export function townTime(epochMs: number, timeZone: string): TownTime {
  const p = wallParts(epochMs, timeZone);
  return { epochMs, dayKey: dayKeyOf(epochMs, timeZone), hourKey: `${ymd(p.year, p.month, p.day)}T${pad(p.hour)}` as HourKey,
    year: p.year, month: p.month, day: p.day, weekday: p.weekday, hour: p.hour, minute: p.minute, minuteOfDay: p.hour * 60 + p.minute };
}
export function dayKeyOf(epochMs: number, timeZone: string): DayKey {
  const p = wallParts(epochMs, timeZone);
  const [y, m, d] = p.hour < tuning.time.dayStartHour ? addDays(p.year, p.month, p.day, -1) : [p.year, p.month, p.day];
  return ymd(y, m, d) as DayKey;
}
export function hourKeyOf(epochMs: number, timeZone: string): HourKey {
  const p = wallParts(epochMs, timeZone);
  return `${ymd(p.year, p.month, p.day)}T${pad(p.hour)}` as HourKey;
}
export function weekKeyOf(dayKey: DayKey): WeekKey {
  const [y, m, d] = dayKey.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7) + 3);       // the Thursday of this ISO week
  const isoYear = date.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((date.getTime() - jan4.getTime()) / 86_400_000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return `${isoYear}-W${pad(week)}` as WeekKey;
}
export function nextLocalTime(epochMs: number, timeZone: string, hour: number, minute = 0): number {
  const p = wallParts(epochMs, timeZone);
  for (let n = 0; n < 3; n++) {
    const [y, m, d] = addDays(p.year, p.month, p.day, n);
    const t = wallToEpoch(y, m, d, hour, minute, timeZone);
    if (t > epochMs) return t;
  }
  throw new Error('nextLocalTime: no instant within three days');
}
export function dayStartMs(dayKey: DayKey, timeZone: string): number {
  const [y, m, d] = dayKey.split('-').map(Number) as [number, number, number];
  return wallToEpoch(y, m, d, tuning.time.dayStartHour, 0, timeZone);
}
const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
export function localToEpoch(local: string, timeZone: string): number {
  const m = LOCAL.exec(local);
  if (!m) throw new Error(`localToEpoch: expected YYYY-MM-DDTHH:mm, got ${local}`);
  const [, y, mo, d, h, mi] = m.map(Number) as [number, number, number, number, number, number];
  return wallToEpoch(y, mo, d, h, mi, timeZone);
}
const SEASONS_NORTH: readonly Season[] = ['winter', 'winter', 'spring', 'spring', 'spring', 'summer', 'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter'];
export function seasonOf(month: number, hemisphere: Hemisphere): Season {
  const m = hemisphere === 'north' ? month : ((month + 5) % 12) + 1;
  return SEASONS_NORTH[m - 1]!;
}
const PRESETS: readonly LightPreset[] = ['morning', 'midday', 'golden', 'dusk'];
export function lightPresetAt(minuteOfDay: number): { a: LightPreset; b: LightPreset; t: number } {
  const starts = tuning.time.presetStarts, blend = tuning.time.lightBlendMinutes, half = blend / 2;
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  for (let i = 0; i < PRESETS.length; i++) {
    const p = PRESETS[i]!, prev = PRESETS[(i + PRESETS.length - 1) % PRESETS.length]!;
    const since = (((m - (starts[p] - half)) % 1440) + 1440) % 1440;
    if (since < blend) return { a: prev, b: p, t: since / blend };
  }
  let cur: LightPreset = 'dusk';
  for (const p of PRESETS) if (m >= starts[p]) cur = p;
  return { a: cur, b: cur, t: 0 };
}
export function isValidTimeZone(tz: string): boolean {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; }
}
